import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { 
  Search, Filter, ChevronLeft, ChevronRight, Eye, 
  CheckCircle, XCircle, Mail, FileText, 
  AlertCircle, Download, ExternalLink, Image as ImageIcon, Plus,
  User, Bot, Send, MessageSquare, ChevronDown, ChevronUp, Paperclip, History,
  DollarSign, RotateCcw, Printer, BookOpen, Edit3, UserCheck, ArrowRight,
  Receipt
} from 'lucide-react';
import api from '../services/api.js';
import { useAuth } from '../App.jsx';
import { useNavigate, useSearchParams } from 'react-router-dom';
import GestionarRepertoriosModal from '../components/GestionarRepertoriosModal';
import CotizacionTramiteModal from '../components/CotizacionTramiteModal.jsx';

import FinalizarTramiteModal from '../components/FinalizarTramiteModal.jsx';
import ConfirmarPagoTramiteModal from '../components/ConfirmarPagoTramiteModal.jsx';
import DevolucionTramiteModal from '../components/DevolucionTramiteModal.jsx';
import SubirBoletaModal from '../components/SubirBoletaModal.jsx';
import FacturacionCardInfo from '../components/FacturacionCardInfo.jsx';
import { formatRepertoriosRange, getComprobantesAceptados } from '../utils/formatters.js';
export { getComprobantesAceptados };
import { handleImprimirTramite } from '../utils/printTramite.js';
import { handleImprimirCotizacionTramite } from '../utils/printTramiteCotizacion.js';
import HistorialCotizacionesModal from '../components/Tramites/HistorialCotizacionesModal.jsx';
import RectificarTramiteModal from '../components/Tramites/RectificarTramiteModal.jsx';
import VisualizadorDocumentosModal from '../components/Tramites/VisualizadorDocumentosModal.jsx';

/* ─── Helpers ──────────────────────────────────────────────────────────── */
const ACTION_LABELS = {
  creacion:                     { label: 'Trámite Ingresado',                color: '#2563eb', bg: '#eff6ff' },
  cambio_estado:                { label: 'Cambio de Estado',                 color: '#7c3aed', bg: '#f5f3ff' },
  asignacion:                   { label: 'Trámite Asignado',                 color: '#0284c7', bg: '#f0f9ff' },
  cambio_estado_y_asignacion:   { label: 'Cambio de Estado y Asignación',    color: '#6366f1', bg: '#eef2ff' },
  envio_cotizacion:             { label: 'Cotización Emitida',               color: '#d97706', bg: '#fffbeb' },
  confirmacion_pago:            { label: 'Pago Confirmado',                  color: '#059669', bg: '#ecfdf5' },
  rechazo_pago:                 { label: 'Pago Rechazado',                   color: '#dc2626', bg: '#fef2f2' },
  devolucion:                   { label: 'Devolución de Fondos',             color: '#d97706', bg: '#fffbeb' },
  adjunto_documento:            { label: 'Documento Adjuntado',              color: '#059669', bg: '#ecfdf5' },
  correo_enviado:               { label: 'Correo Enviado al Ciudadano',      color: '#d97706', bg: '#fffbeb' },
  respuesta_ciudadano:          { label: 'Respuesta del Ciudadano',          color: '#0891b2', bg: '#ecfeff' },
  rectificacion_tipo:           { label: 'Rectificación de Acto / Inscripción', color: '#ea580c', bg: '#fff7ed' },
  marcado_exento_repertorio:    { label: 'Marcado Exento de Repertorio',     color: '#4f46e5', bg: '#eef2ff' },
  desmarcado_exento_repertorio: { label: 'Desmarcado de Exención',           color: '#64748b', bg: '#f1f5f9' },
  reapertura_staff:             { label: 'Trámite Reabierto',                color: '#ea580c', bg: '#fff7ed' },
  entrega_documentos:           { label: 'Documentos Entregados',           color: '#059669', bg: '#ecfdf5' },
  rechazo:                      { label: 'Trámite Rechazado',                color: '#dc2626', bg: '#fef2f2' },
  actualizacion:                { label: 'Actualización de Datos',           color: '#64748b', bg: '#f1f5f9' },
};

function getBadgeClass(estadoNombre) {
  const txt = (estadoNombre || '').toLowerCase();
  if (txt.includes('pendiente correo')) return 'badge-warning';
  if (txt.includes('ingresado'))         return 'badge-info';
  if (txt.includes('proces'))           return 'badge-warning';
  if (txt.includes('recibido'))         return 'badge-info';
  if (txt.includes('pagado'))           return 'badge-success';
  if (txt.includes('pago'))             return 'badge-warning';
  if (txt.includes('complet'))         return 'badge-success';
  if (txt.includes('rechaz'))          return 'badge-error';
  if (txt.includes('rehus'))           return 'badge-error';
  if (txt.includes('reabierto'))        return 'badge-copper';
  if (txt.includes('devuelto'))         return 'badge-warning';
  return 'badge-gray';
}


function formatWorkerName(worker, payload = {}) {
  if (payload?.usuario || payload?.funcionario) {
    const u = String(payload.usuario || payload.funcionario).trim();
    if (u.toLowerCase().includes('admin')) return 'Sistema';
    return u;
  }
  if (!worker) return 'Sistema';
  const name = (typeof worker === 'string' ? worker : (worker.name || '')).trim();
  const lower = name.toLowerCase();
  const role = (worker.role || '').toLowerCase();
  if (!name || lower.includes('admin') || role === 'admin' || role === 'super_admin' || worker.id === 1) {
    return 'Sistema';
  }
  return name;
}

/* ─── Sub-componente: Panel de Historial ───────────────────────────────── */
function HistorialPanel({ steps = [], onReply, usuarios = [], estados = [] }) {
  if (!steps.length) {
    return <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Sin historial registrado.</p>;
  }

  const resolveEstado = (val) => {
    if (!val) return '';
    if (typeof val === 'string' && isNaN(Number(val))) return val;
    const match = estados.find(e => String(e.id) === String(val));
    return match ? match.nombre : (typeof val === 'string' ? val : `Estado #${val}`);
  };

  const resolveUsuario = (val) => {
    if (!val) return '';
    if (typeof val === 'string' && isNaN(Number(val))) return val;
    const match = usuarios.find(u => String(u.id) === String(val));
    return match ? match.name : (typeof val === 'string' ? val : `Usuario #${val}`);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
      {steps.map(step => {
        const meta = ACTION_LABELS[step.action] || ACTION_LABELS.actualizacion;
        const esCiudadano = step.action === 'respuesta_ciudadano';
        return (
          <div key={step.id} style={{
            display: 'flex', gap: '0.75rem', alignItems: 'flex-start',
          }}>
            <div style={{
              width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
              background: meta.bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {esCiudadano
                ? <User size={15} color={meta.color} />
                : <Bot size={15} color={meta.color} />}
            </div>
            <div style={{
              flex: 1, background: meta.bg, borderRadius: 10, padding: '0.6rem 0.9rem',
              border: `1px solid ${meta.color}22`,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <span style={{ fontWeight: 700, fontSize: '0.78rem', color: meta.color }}>
                  {meta.label}
                </span>
                <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                  {new Date(step.created_at).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' })}
                  {!esCiudadano && (
                    <span style={{ marginLeft: 6 }}>
                      · {step.display_worker_name || formatWorkerName(step.worker, step.payload)}
                    </span>
                  )}
                </span>
              </div>
              {step.payload?.asunto && (
                <div style={{ fontSize: '0.82rem', marginBottom: 2 }}>
                  <strong>Asunto:</strong> {step.payload.asunto}
                </div>
              )}
              {(step.payload?.mensaje || step.payload?.comentario) && (
                <div style={{ fontSize: '0.82rem', color: '#374151', whiteSpace: 'pre-wrap' }}>
                  {step.payload.mensaje || step.payload.comentario}
                </div>
              )}
              {step.payload?.nota && (
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                  {step.payload.nota}
                </div>
              )}

              {/* Detalle de Asignación */}
              {(step.action === 'asignacion' || step.action === 'cambio_estado_y_asignacion' || step.payload?.changes?.asignado_a) && (
                <div style={{ fontSize: '0.8rem', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6, color: '#374151' }}>
                  <UserCheck size={13} style={{ color: '#0284c7', flexShrink: 0 }} />
                  <span>
                    <strong>Asignado a:</strong>{' '}
                    <strong style={{ color: '#0369a1' }}>
                      {resolveUsuario(step.payload?.changes?.asignado_a?.new || step.payload?.funcionario || 'Funcionario')}
                    </strong>
                    {step.payload?.changes?.asignado_a?.old && step.payload?.changes?.asignado_a?.old !== 'Sin asignar' && (
                      <span style={{ color: 'var(--text-muted)', fontSize: '0.74rem', marginLeft: 6 }}>
                        (anteriormente: {resolveUsuario(step.payload.changes.asignado_a.old)})
                      </span>
                    )}
                  </span>
                </div>
              )}

              {/* Detalle de Cambio de Estado */}
              {(step.action === 'cambio_estado' || step.action === 'cambio_estado_y_asignacion' || step.payload?.changes?.estado) && (
                <div style={{ fontSize: '0.8rem', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6, color: '#374151' }}>
                  <ArrowRight size={12} style={{ color: '#7c3aed', flexShrink: 0 }} />
                  <span>
                    {step.payload?.changes?.estado ? (
                      <>
                        <span style={{ color: 'var(--text-muted)' }}>{resolveEstado(step.payload.changes.estado.old)}</span>
                        <span style={{ margin: '0 4px', color: '#7c3aed', fontWeight: 700 }}>➔</span>
                        <strong style={{ color: '#5b21b6' }}>{resolveEstado(step.payload.changes.estado.new)}</strong>
                      </>
                    ) : (step.old_state && step.new_state && step.old_state !== step.new_state) ? (
                      <>
                        <span style={{ color: 'var(--text-muted)' }}>{resolveEstado(step.old_state)}</span>
                        <span style={{ margin: '0 4px', color: '#7c3aed', fontWeight: 700 }}>➔</span>
                        <strong style={{ color: '#5b21b6' }}>{resolveEstado(step.new_state)}</strong>
                      </>
                    ) : (
                      <strong>{resolveEstado(step.new_state) || 'Estado actualizado'}</strong>
                    )}
                  </span>
                </div>
              )}

              {/* Detalle de Cotización */}
              {step.action === 'envio_cotizacion' && (
                <div style={{ fontSize: '0.8rem', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6, color: '#374151' }}>
                  <DollarSign size={13} style={{ color: '#d97706', flexShrink: 0 }} />
                  <span>
                    <strong>Total Cotizado:</strong> ${Number(step.payload?.total || 0).toLocaleString('es-CL')}
                    {step.payload?.caja && (
                      <span style={{ marginLeft: 6, color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                        · Caja: {step.payload.caja}
                      </span>
                    )}
                  </span>
                </div>
              )}

              {/* Detalle de Confirmación de Pago */}
              {step.action === 'confirmacion_pago' && (
                <div style={{ fontSize: '0.8rem', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6, color: '#059669' }}>
                  <CheckCircle size={13} style={{ flexShrink: 0 }} />
                  <span>
                    <strong>Monto confirmado:</strong> ${Number(step.payload?.total || step.payload?.monto || 0).toLocaleString('es-CL')}
                  </span>
                </div>
              )}

              {/* Otros campos modificados */}
              {step.payload?.changes && Object.keys(step.payload.changes).filter(k => !['estado', 'estado_id', 'asignado_a'].includes(k)).length > 0 && (
                <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 3, fontSize: '0.78rem' }}>
                  {Object.entries(step.payload.changes)
                    .filter(([k]) => !['estado', 'estado_id', 'asignado_a'].includes(k))
                    .map(([field, val]) => (
                      <div key={field} style={{ background: '#ffffff', padding: '3px 8px', borderRadius: 4, border: '1px solid #e2e8f0', color: '#334155' }}>
                        <strong style={{ textTransform: 'capitalize' }}>{field.replace(/_/g, ' ')}:</strong>{' '}
                        <span style={{ color: '#94a3b8', textDecoration: 'line-through' }}>{String(val?.old ?? '—')}</span>
                        <span style={{ margin: '0 4px', color: '#64748b' }}>➔</span>
                        <strong style={{ color: '#0f172a' }}>{String(val?.new ?? '—')}</strong>
                      </div>
                    ))}
                </div>
              )}

              {/* Comentarios de auditoría adicionales */}
              {step.comments && !step.payload?.mensaje && !step.payload?.comentario && step.action !== 'rectificacion_tipo' && (
                <div style={{ fontSize: '0.78rem', color: '#475569', marginTop: 4, fontStyle: 'italic' }}>
                  {step.comments}
                </div>
              )}

              {step.action === 'rectificacion_tipo' && (
                <div style={{ marginTop: 6, background: '#ffffff', padding: '8px 10px', borderRadius: 8, border: '1px solid #fed7aa', fontSize: '0.8rem' }}>
                  <div style={{ color: '#c2410c', fontWeight: 700, marginBottom: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Edit3 size={12} /> Modificación del Acto / Inscripción:
                  </div>
                  <div style={{ color: '#64748b', textDecoration: 'line-through', fontSize: '0.76rem' }}>
                    Anterior: {step.payload?.tipo_anterior || step.old_state} ({step.payload?.area_anterior || 'General'})
                  </div>
                  <div style={{ color: '#15803d', fontWeight: 600, fontSize: '0.8rem', marginTop: 2 }}>
                    Rectificado a: {step.payload?.tipo_nuevo || step.new_state} ({step.payload?.area_nueva || 'General'})
                  </div>
                  {step.payload?.motivo && (
                    <div style={{ color: '#475569', fontSize: '0.76rem', marginTop: 4, background: '#fff7ed', padding: '4px 6px', borderRadius: 4 }}>
                      <strong>Motivo:</strong> {step.payload.motivo}
                    </div>
                  )}
                </div>
              )}

              {esCiudadano && onReply && (
                <div style={{ marginTop: '0.5rem', display: 'flex', justifyContent: 'flex-end' }}>
                   <button
                    className="btn btn-sm"
                    style={{
                      padding: '2px 10px',
                      fontSize: '0.7rem',
                      background: 'white',
                      color: meta.color,
                      border: `1px solid ${meta.color}`,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4
                    }}
                    onClick={() => onReply(step)}
                   >
                     <Send size={10} /> Responder
                   </button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ─── Sub-componente: Modal Correo ─────────────────────────────────────── */
function ModalCorreo({ item, replyTo, onClose, onSent, type = 'tramite' }) {
  const [asunto, setAsunto]   = useState(replyTo ? `Re: ${replyTo.payload?.asunto || 'Tu mensaje'}` : '');
  const [mensaje, setMensaje] = useState('');
  const [sending, setSending] = useState(false);

  const endpoint = type === 'tramite' ? `/tramites/${item.id}/correo` : `/solicitudes/${item.id}/correo`;

  const handleSend = async () => {
    if (!mensaje.trim()) {
      toast.error('El mensaje es obligatorio.');
      return;
    }
    setSending(true);
    try {
      await api.post(endpoint, {
        asunto: asunto || 'Respuesta a tu requerimiento',
        mensaje,
        reply_to_step_id: replyTo?.id
      });
      toast.success('Correo enviado al ciudadano y registrado en el historial.');
      onSent();
      onClose();
    } catch {
      toast.error('Error al enviar el correo.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 style={{ fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Send size={18} color="var(--primary)" /> Enviar Correo al Ciudadano
          </h2>
          <button className="modal-close" onClick={onClose}><XCircle size={18} /></button>
        </div>

        <p style={{ fontSize: '0.83rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
          Para: <strong>{item.nombre_solicitante}</strong> ({item.correo || item.email})
          &nbsp;·&nbsp;{type === 'tramite' ? 'Trámite' : 'Solicitud'} <strong>{item.numero_seguimiento}</strong>
        </p>

        {replyTo && (
          <div style={{
            background: '#f8fafc', padding: '0.8rem', borderRadius: 10, marginBottom: '1.2rem',
            borderLeft: '4px solid var(--primary)', border: '1px solid #e2e8f0', borderLeftWidth: 4
          }}>
            <div style={{ fontSize: '0.65rem', color: 'var(--primary)', fontWeight: 800, marginBottom: 4, textTransform: 'uppercase' }}>
              Mensaje del Ciudadano:
            </div>
            <div style={{ fontSize: '0.8rem', color: '#475569', fontStyle: 'italic', lineHeight: 1.4 }}>
              "{replyTo.payload?.mensaje || replyTo.payload?.comentario}"
            </div>
          </div>
        )}

        <div className="form-group">
          <label>Asunto</label>
          <input value={asunto} onChange={e => setAsunto(e.target.value)} placeholder="Ej: Información adicional requerida" />
        </div>
        <div className="form-group">
          <label>Mensaje de Respuesta</label>
          <textarea rows={5} value={mensaje} onChange={e => setMensaje(e.target.value)}
            placeholder="Escribe aquí tu respuesta para el ciudadano..." />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={sending}>Cancelar</button>
          <button className="btn btn-primary" onClick={handleSend} disabled={sending}>
            {sending ? <span className="spinner" style={{ width: 16, height: 16 }} /> : <><Send size={14} /> Enviar Respuesta</>}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PortalTramites() {
  const { user, isStaff, hasRole } = useAuth();
  const navigate = useNavigate();
  const canAssign = hasRole('boss') || hasRole('super_admin') || hasRole('admin') || Boolean(user?.can_assign_tramites);
  
  // Data States
  const [tramites, setTramites] = useState([]);
  const [areas, setAreas] = useState([]);
  const [estados, setEstados] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [searchParams] = useSearchParams();
  const initialSearch = searchParams.get('search') || '';

  // Pagination & Filtering
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({ 
    search: initialSearch,
    estado_id: '', 
    area_id: '',
    mine: false,
    hideCompleted: initialSearch ? false : true
  });
  
  // Modals
  const [rejectionModal, setRejectionModal] = useState(null); // stores the tramite to reject
  const [rejectionMotive, setRejectionMotive] = useState('');
  const [rejectionFile, setRejectionFile] = useState(null); // Documento oficial for rehusamiento
  const [docModal, setDocModal] = useState(null); // stores the tramite whose docs we're viewing
  const [selectedDoc, setSelectedDoc] = useState(null); // the specific doc being visualized
  const [repertorioModal, setRepertorioModal] = useState({ isOpen: false, tramite: null });
  const [cotizacionModal, setCotizacionModal] = useState({ isOpen: false, tramite: null });
  const [finalizarModal, setFinalizarModal] = useState({ isOpen: false, tramite: null });
  const [confirmarPagoModal, setConfirmarPagoModal] = useState({ isOpen: false, tramite: null });
  const [devolucionModal, setDevolucionModal] = useState({ isOpen: false, tramite: null });
  const [historialCotizModal, setHistorialCotizModal] = useState({ isOpen: false, tramite: null });
  const [boletaModal, setBoletaModal] = useState({ isOpen: false, item: null, type: 'tramite' });
  const [modalRectificar, setModalRectificar] = useState(null);

  const handleTramiteRectificado = (updatedTramite) => {
    if (!updatedTramite) return;
    const docCount = updatedTramite.documentos?.length ?? updatedTramite.documentos_count ?? 0;
    const enriched = {
      ...updatedTramite,
      documentos_count: docCount,
    };
    setTramites(prev => prev.map(t => t.id === updatedTramite.id ? { ...t, ...enriched } : t));
    setExpandedDetail(prev => {
      const existing = prev[updatedTramite.id] || {};
      return {
        ...prev,
        [updatedTramite.id]: {
          ...existing,
          ...enriched,
          documentos: updatedTramite.documentos || existing.documentos,
          workflow_steps: updatedTramite.workflow_steps || updatedTramite.workflowSteps || existing.workflow_steps
        }
      };
    });
  };

  // v2 States
  const [expandedId, setExpandedId] = useState(null);
  const [expandedDetail, setExpandedDetail] = useState({}); // { [id]: tramite completo }
  const [modalCorreo, setModalCorreo] = useState(null);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    fetchTramites();
  }, [page, filters.estado_id, filters.area_id, filters.mine, filters.hideCompleted]);

  const fetchInitialData = async () => {
    try {
      const [resAreas, resEstados, resUsers] = await Promise.all([
        api.get('/tramites/areas'),
        api.get('/estados'),
        api.get('/users/assignable')
      ]);
      setAreas(resAreas.data);
      // Filter states relevant to tramites if they have a 'tipo' column
      setEstados(resEstados.data.filter(e => e.tipo === 'tramite' || !e.tipo));
      const userList = Array.isArray(resUsers.data) ? resUsers.data : (resUsers.data?.data || []);
      setUsuarios(userList);

      // Deep linking: auto-expand if 'tramite' param exists
      const params = new URLSearchParams(window.location.search);
      const tId = params.get('tramite');
      if (tId) {
        setExpandedId(parseInt(tId));
        try {
          const { data } = await api.get(`/tramites/${tId}`);
          setExpandedDetail(prev => ({ ...prev, [tId]: data }));
        } catch (e) {
          console.error("Error loading deep link tramite", e);
        }
      }
    } catch (err) {
      toast.error('Error al cargar datos auxiliares.');
    }
  };

  const fetchTramites = async () => {
    setLoading(true);
    try {
      const res = await api.get('/tramites', {
        params: {
          page,
          search: filters.search,
          estado_id: filters.estado_id,
          area_id: filters.area_id,
          mine: filters.mine ? 1 : 0,
          hide_completed: filters.hideCompleted ? 1 : 0
        }
      });
      setTramites(res.data.data);
      setTotalPages(res.data.last_page);
    } catch (err) {
      toast.error('Error al cargar la lista de trámites.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchTramites();
  };

  const updateStatus = async (tramiteId, estadoId) => {
    try {
      await api.patch(`/tramites/${tramiteId}/estado`, { estado_id: estadoId });
      toast.success('Estado actualizado.');
      fetchTramites();
    } catch (err) {
      toast.error('Error al actualizar estado.');
    }
  };

  const handleAssign = async (tramiteId, staffId) => {
    const newId = staffId ? Number(staffId) : null;
    setTramites(prev => prev.map(t => t.id === tramiteId ? { ...t, asignado_a: newId } : t));
    try {
      const res = await api.patch(`/tramites/${tramiteId}/asignar`, {
        asignado_a: newId,
      });
      toast.success(res.data?.message || (staffId ? 'Trámite asignado con éxito.' : 'Asignación removida.'));
      fetchTramites();
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.message || 'Error al asignar el trámite.');
      fetchTramites();
    }
  };

  const handleReject = async () => {
    if (!rejectionMotive.trim()) return toast.error('Ingrese un motivo.');

    try {
      const formData = new FormData();
      formData.append('motivo', rejectionMotive);
      if (rejectionFile) {
        formData.append('documento_oficial', rejectionFile);
      }

      await api.post(`/tramites/${rejectionModal.id}/rehusar`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Trámite rehusado exitosamente.');
      setRejectionModal(null);
      setRejectionMotive('');
      setRejectionFile(null);
      fetchTramites();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al procesar el rehusamiento.');
    }
  };

  const handleNotify = async (tramiteId) => {
    try {
      await api.post(`/tramites/${tramiteId}/notificar`);
      toast.success('Notificación enviada al usuario.');
    } catch (err) {
      toast.error('Error al enviar notificación.');
    }
  };

  const handleOpenDocs = async (tramite) => {
    try {
      toast.loading('Cargando documentos...', { id: 'fetch-docs' });
      const res = await api.get(`/tramites/${tramite.id}`);
      setDocModal(res.data);
      setSelectedDoc(null);
      toast.success('Documentos cargados', { id: 'fetch-docs' });
    } catch (err) {
      toast.error('Error al cargar documentos del trámite.', { id: 'fetch-docs' });
    }
  };

  const cargarDetalle = async (tramite) => {
    try {
      const { data } = await api.get(`/tramites/${tramite.id}`);
      setExpandedDetail(prev => ({ ...prev, [tramite.id]: data }));
    } catch {
      setExpandedDetail(prev => ({ ...prev, [tramite.id]: tramite }));
    }
  };

  const toggleExpand = (tramite) => {
    if (expandedId === tramite.id) {
      setExpandedId(null);
    } else {
      setExpandedId(tramite.id);
      cargarDetalle(tramite);
    }
  };

  // Helper to determine file type
  const getFileType = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return 'image';
    if (ext === 'pdf') return 'pdf';
    if (ext === 'docx' || ext === 'doc') return 'word';
    return 'other';
  };

  const getDocUrl = (token) => {
    // Assuming the backend download route handles authentication or the token is enough
    const API = import.meta.env.VITE_API_URL || 'https://cbrsancarlos.cl';
    return `${API}/api/v1/tramites/documentos/${token}`;
    const baseUrl = import.meta.env.VITE_API_URL?.replace(/\/$/, '');
    return `${baseUrl}/api/v1/tramites/documentos/${token}`;
  };

  const getWordViewerUrl = (token) => {
    const directUrl = getDocUrl(token);
    // Office Online Viewer requires a publicly accessible URL. 
    // If local/private, this won't work perfectly without a proxy, but it's the standard way.
    return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(directUrl)}`;
  };

  const getRemainingDays = (tramite) => {
    if (!tramite.repertorios || tramite.repertorios.length === 0) return null;
    if (tramite.estado?.nombre === 'Completado' || tramite.estado?.nombre === 'Rechazado' || tramite.estado?.nombre === 'Rehusado') return null;

    const dates = tramite.repertorios
      .map(r => r.fecha_repertorio)
      .filter(Boolean)
      .map(d => new Date(d));

    if (dates.length === 0) return null;

    const mostRecent = new Date(Math.max(...dates));
    const expirationDate = new Date(mostRecent);
    expirationDate.setDate(expirationDate.getDate() + 20); // 20 days de corrido

    const today = new Date();
    // Reset hours to compare only dates
    today.setHours(0, 0, 0, 0);
    expirationDate.setHours(0, 0, 0, 0);

    const diffTime = expirationDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;
  };

  return (
    <div className="container" style={{ maxWidth: '100%', padding: '2rem clamp(16px, 4vw, 48px)', minHeight: '80vh' }}>
      
      {/* HEADER & CONTROLS */}
      <div className="animate-fade-in" style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h1 className="section-title" style={{ marginBottom: '0.5rem' }}>Gestión de Trámites</h1>
            <p className="text-muted">Administración y revisión de inscripciones ingresadas por ciudadanos.</p>
          </div>
          
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
            {isStaff?.() && (
              <button 
                className="btn btn-primary" 
                style={{ padding: '0.6rem 1.2rem', gap: '8px', display: 'flex', alignItems: 'center', background: 'var(--accent)', borderColor: 'var(--accent)', color: 'white' }}
                onClick={() => navigate('/ingresotramite')}
              >
                <Plus size={18} /> Nuevo Trámite (Interno)
              </button>
            )}

            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
             <form onSubmit={handleSearch} style={{ position: 'relative' }}>
                <Search size={18} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                <input 
                  type="text" 
                  placeholder="Buscar Solicitante, RUT, Seguimiento..." 
                  className="form-control"
                  style={{ paddingLeft: '2.5rem', minWidth: '300px' }}
                  value={filters.search}
                  onChange={(e) => setFilters(p => ({ ...p, search: e.target.value }))}
                />
             </form>

             <select 
               className="form-control" 
               style={{ width: 'auto' }}
               value={filters.estado_id}
               onChange={(e) => setFilters(p => ({ ...p, estado_id: e.target.value }))}
             >
               <option value="">Todos los Estados</option>
               {estados.map(e => <option key={e.id} value={e.id}>{e.nombre}</option>)}
             </select>

              <select 
                className="form-control" 
                style={{ width: 'auto' }}
                value={filters.area_id}
                onChange={(e) => setFilters(p => ({ ...p, area_id: e.target.value }))}
              >
                <option value="">Todas las Áreas</option>
                {areas.map(a => <option key={a.id} value={a.id}>{a.nombre}</option>)}
              </select>

              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                background: filters.mine ? 'var(--primary-light)' : '#f1f5f9',
                color: filters.mine ? 'white' : 'var(--text-main)',
                padding: '0.6rem 1.2rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: 600,
                transition: 'all 0.2s'
              }}>
                <input
                  type="checkbox"
                  checked={filters.mine}
                  onChange={(e) => setFilters(p => ({ ...p, mine: e.target.checked }))}
                  style={{ width: '16px', height: '16px' }}
                />
                Solo Mis Trámites
              </label>

              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                background: filters.hideCompleted ? '#fee2e2' : '#f1f5f9',
                color: filters.hideCompleted ? '#991b1b' : 'var(--text-main)',
                padding: '0.6rem 1.2rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: 600,
                transition: 'all 0.2s'
              }}>
                <input
                  type="checkbox"
                  checked={filters.hideCompleted}
                  onChange={(e) => setFilters(p => ({ ...p, hideCompleted: e.target.checked }))}
                  style={{ width: '16px', height: '16px' }}
                />
                Ocultar Completados
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* CARDS LIST (v2 Accordion) */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '4rem' }}><span className="spinner spinner-lg" /></div>
      ) : tramites.length === 0 ? (
        <div className="glass-card" style={{ textAlign: 'center', padding: '5rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎉</div>
          <h3>¡Todo al día!</h3>
          <p className="text-muted">No hay trámites pendientes en esta vista.</p>
        </div>
      ) : (
        <div className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
          {tramites.map(t => {
            const comprobantesAceptados = getComprobantesAceptados(t, expandedDetail[t.id]);
            return (
            <div key={t.id} className="glass-card" style={{ padding: 0, overflow: 'hidden' }}>

              {/* Card Summary (Click to expand) */}
              <div
                style={{
                  padding: '0.85rem 1.2rem', display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', cursor: 'pointer',
                  background: expandedId === t.id ? '#f8fafc' : 'transparent',
                }}
                onClick={() => toggleExpand(t)}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', gap: '0.7rem', alignItems: 'center', marginBottom: '0.2rem', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 800, color: 'var(--primary-dark)', fontSize: '0.95rem' }}>
                      {t.nombre_solicitante}
                    </span>
                    <span style={{ fontSize: '0.8rem', color: 'var(--gray-500)', fontFamily: 'monospace', fontWeight: 600 }}>
                      {t.numero_seguimiento}
                    </span>
                    <span className={`badge ${getBadgeClass(t.estado?.nombre)}`}
                      style={{ textTransform: 'uppercase', padding: '2px 8px', fontSize: '0.63rem', borderRadius: 4 }}>
                      {t.estado?.nombre || 'Desconocido'}
                    </span>
                    {t.exento_repertorio ? (
                      <span 
                        title={`Trámite exento de repertorio previo${t.motivo_exento_repertorio ? `: ${t.motivo_exento_repertorio}` : ''}`} 
                        style={{ color: '#047857', background: '#d1fae5', padding: '2px 8px', borderRadius: 4, fontSize: '0.72rem', display: 'flex', alignItems: 'center', gap: 4, fontWeight: 700 }}
                      >
                         🛡️ Exento de Repertorio
                      </span>
                    ) : (t.repertorios && t.repertorios.length > 0 && (
                      <span 
                        title={`Repertorios: ${t.repertorios.map(r => r.numero_repertorio).join(', ')} (${t.repertorios.length} en total)`} 
                        style={{ color: 'var(--copper)', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 3, fontWeight: 700 }}
                      >
                         📚 {formatRepertoriosRange(t.repertorios)}
                      </span>
                    ))}

                    {/* Assignment Indicators */}
                    {t.asignado_a ? (
                      <span className="badge" style={{ fontSize: '0.65rem', background: t.asignado_a === user?.id ? '#3b82f6' : '#f1f5f9', color: t.asignado_a === user?.id ? 'white' : '#475569', borderRadius: '4px', border: '1px solid #cbd5e1' }}>
                        ⚙️ {t.asignado_a === user?.id ? 'Gestión (Tú)' : (t.assigned_to?.name || 'Asignado')}
                      </span>
                    ) : (
                      <span className="badge" style={{ fontSize: '0.65rem', background: '#fef3c7', color: '#92400e', borderRadius: '4px', border: '1px solid #fde68a' }}>
                        👤 Sin Asignar
                      </span>
                    )}
                    {t.cotizacion?.usuario_caja_id === user?.id && (
                      <span className="badge badge-success" style={{ fontSize: '0.65rem', background: '#10b981', color: 'white', borderRadius: '4px' }}>
                        💰 Caja (Tú)
                      </span>
                    )}
                  </div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.78rem', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <span>RUT: {t.rut_solicitante}</span>
                    <span>· {new Date(t.created_at).toLocaleDateString('es-CL')}</span>
                    <span className="badge badge-gray" style={{ fontSize: '0.65rem' }}>{t.area?.nombre || 'General'}</span>
                    {getRemainingDays(t) !== null && (
                       <span style={{
                         color: getRemainingDays(t) <= 5 ? 'var(--error)' : 'var(--success)',
                         fontWeight: 700
                       }}>
                         ⏱️ {getRemainingDays(t)} días restantes
                       </span>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                   {expandedId === t.id ? <ChevronUp size={20} color="var(--primary)" /> : <ChevronDown size={20} color="var(--primary)" />}
                </div>
              </div>

              {/* Expanded Details */}
              {expandedId === t.id && (
                <div style={{ borderTop: '1px solid var(--border-color)', background: '#f8fafc' }}>

                  {/* Banner de Trámite Rehusado */}
                  {t.estado?.nombre === 'Rehusado' && (
                    <div style={{
                      margin: '1rem 1.2rem 0',
                      padding: '12px 16px',
                      background: '#fef2f2',
                      border: '1px solid #fecaca',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: '12px',
                      flexWrap: 'wrap'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', flex: 1, minWidth: '260px' }}>
                        <XCircle size={22} color="#dc2626" style={{ flexShrink: 0, marginTop: '2px' }} />
                        <div>
                          <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#991b1b' }}>
                            Trámite Rehusado
                          </div>
                          <div style={{ fontSize: '0.84rem', color: '#7f1d1d', marginTop: '3px', whiteSpace: 'pre-wrap' }}>
                            <strong>Motivo de rehúso:</strong> {t.motivo_rehusado || 'Sin detalle de motivo registrado.'}
                          </div>
                        </div>
                      </div>
                      {t.documento_rehusado_url && (
                        <a
                          href={t.documento_rehusado_url}
                          target="_blank"
                          rel="noreferrer"
                          className="btn btn-sm"
                          style={{ background: '#dc2626', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.78rem', textDecoration: 'none' }}
                        >
                          <FileText size={14} /> Ver Documento Oficial de Rehúso
                        </a>
                      )}
                    </div>
                  )}

                  {/* Fila 1: Metadatos y Selectores de Control (Gestión + Estado) */}
                  <div style={{
                    padding: '0.85rem 1.25rem',
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '1rem',
                    borderBottom: '1px solid #f1f5f9',
                    backgroundColor: '#ffffff'
                  }}>
                    {/* Meta Solicitante */}
                    <div style={{ fontSize: '0.83rem', color: '#475569', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <Mail size={13} color="#64748b" /> <strong>Email:</strong> {t.correo || 'N/A'}
                      </span>
                      <span style={{ color: '#cbd5e1' }}>·</span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <FileText size={13} color="#64748b" /> <strong>Tipo:</strong> {t.tipo_inscripcion?.nombre || '—'}
                        <button
                          type="button"
                          onClick={() => setModalRectificar(t)}
                          title="Rectificar tipo de trámite o corregir error de ingreso"
                          style={{
                            marginLeft: 4,
                            background: '#fff7ed',
                            color: '#ea580c',
                            border: '1px solid #fed7aa',
                            borderRadius: '4px',
                            padding: '1px 6px',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 3
                          }}
                        >
                          <Edit3 size={10} /> Corregir
                        </button>
                      </span>
                      {t.observaciones && (
                        <div style={{ width: '100%', marginTop: 2, color: '#b45309', fontSize: '0.8rem', fontStyle: 'italic' }}>
                          Obs: {t.observaciones}
                        </div>
                      )}
                    </div>

                    {/* Selectores de Control (Gestión + Estado) en una sola fila compacta */}
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', flexWrap: 'nowrap' }}>
                      {/* Direct Assignment Dropdown or Self-Claim Button */}
                      {canAssign ? (
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                          <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Gestión:</span>
                          <select
                            value={t.asignado_a || ''}
                            onChange={e => handleAssign(t.id, e.target.value)}
                            style={{
                              padding: '5px 8px',
                              fontSize: '0.8rem',
                              borderRadius: 6,
                              minWidth: '150px',
                              maxWidth: '185px',
                              border: t.asignado_a ? '1px solid #93c5fd' : '1px solid #cbd5e1',
                              backgroundColor: t.asignado_a ? '#eff6ff' : '#ffffff',
                              color: t.asignado_a ? '#1d4ed8' : '#475569',
                              fontWeight: t.asignado_a ? 600 : 400,
                              cursor: 'pointer'
                            }}
                            title="Asignar o reasignar funcionario responsable del trámite"
                          >
                            <option value="">👤 Sin Asignar</option>
                            {usuarios.map(u => (
                              <option key={u.id} value={u.id}>{u.name}</option>
                            ))}
                          </select>
                        </div>
                      ) : !t.asignado_a ? (
                        <button
                          type="button"
                          className="btn btn-sm"
                          style={{
                            background: '#eff6ff',
                            color: '#1d4ed8',
                            border: '1px solid #bfdbfe',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 5,
                            fontSize: '0.78rem',
                            fontWeight: 600,
                            padding: '4px 10px'
                          }}
                          onClick={() => handleAssign(t.id, user.id)}
                          title="Autoasignarte este trámite para procesarlo"
                        >
                          ➕ Tomar trámite
                        </button>
                      ) : (
                        <div style={{
                          fontSize: '0.78rem',
                          padding: '4px 9px',
                          borderRadius: 6,
                          backgroundColor: t.asignado_a === user?.id ? '#eff6ff' : '#f8fafc',
                          color: t.asignado_a === user?.id ? '#1d4ed8' : '#475569',
                          fontWeight: t.asignado_a === user?.id ? 700 : 500,
                          border: '1px solid #e2e8f0',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 5
                        }}>
                          👤 {t.asignado_a === user?.id ? 'Asignado a ti' : (t.assigned_to?.name || 'Asignado')}
                        </div>
                      )}

                      {/* Change State Dropdown */}
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Estado:</span>
                        <select
                          value={t.estado_id || ''}
                          onChange={e => updateStatus(t.id, e.target.value)}
                          style={{
                            padding: '5px 8px',
                            fontSize: '0.8rem',
                            borderRadius: 6,
                            minWidth: '160px',
                            maxWidth: '195px',
                            border: '1px solid #cbd5e1',
                            backgroundColor: '#ffffff',
                            color: '#0f172a',
                            fontWeight: 600,
                            cursor: 'pointer'
                          }}
                        >
                          <option value="" disabled>Cambiar Estado...</option>
                          {estados.map(est => (
                            <option key={est.id} value={est.id}>{est.nombre}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Fila 2: Barra de Acciones Inteligente con Jerarquía Visual */}
                  <div style={{
                    padding: '0.75rem 1.25rem',
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '0.75rem',
                    borderBottom: '1px solid var(--border-color)',
                    backgroundColor: '#f8fafc'
                  }}>
                    {/* Grupo Izquierdo: Herramientas Operativas y Consulta con colores distintivos */}
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                      {/* Correo - Azul */}
                      <button 
                        type="button"
                        className="btn btn-sm"
                        style={{
                          background: '#1d4ed8',
                          color: '#ffffff',
                          border: 'none',
                          borderRadius: '6px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 5,
                          fontSize: '0.76rem',
                          fontWeight: 700,
                          padding: '5px 10px',
                          boxShadow: '0 1px 2px rgba(29, 78, 216, 0.25)'
                        }}
                        onClick={() => setModalCorreo(t)}
                        title="Enviar correo al ciudadano"
                      >
                        <Send size={13} color="#ffffff" /> Correo
                      </button>

                      {/* Reptos. - Índigo */}
                      <button
                        type="button"
                        className="btn btn-sm"
                        style={{
                          background: '#4338ca',
                          color: '#ffffff',
                          border: 'none',
                          borderRadius: '6px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 5,
                          fontSize: '0.76rem',
                          fontWeight: 700,
                          padding: '5px 10px',
                          boxShadow: '0 1px 2px rgba(67, 56, 202, 0.25)'
                        }}
                        onClick={() => setRepertorioModal({ isOpen: true, tramite: t })}
                        title="Gestionar números de repertorio"
                      >
                        <BookOpen size={13} color="#ffffff" /> Reptos.
                      </button>

                      {/* Imprimir Ficha - Gris Pizarra */}
                      <button
                        type="button"
                        className="btn btn-sm"
                        style={{
                          background: '#475569',
                          color: '#ffffff',
                          border: 'none',
                          borderRadius: '6px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 5,
                          fontSize: '0.76rem',
                          fontWeight: 700,
                          padding: '5px 10px',
                          boxShadow: '0 1px 2px rgba(71, 85, 105, 0.25)'
                        }}
                        onClick={() => handleImprimirTramite(t)}
                        title="Imprimir Información y Ficha del Trámite"
                      >
                        <Printer size={13} color="#ffffff" /> Imprimir
                      </button>

                      {/* Rectificar - Naranja */}
                      <button
                        type="button"
                        className="btn btn-sm"
                        style={{
                          background: '#ea580c',
                          color: '#ffffff',
                          border: 'none',
                          borderRadius: '6px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 5,
                          fontSize: '0.76rem',
                          fontWeight: 700,
                          padding: '5px 10px',
                          boxShadow: '0 1px 2px rgba(234, 88, 12, 0.25)'
                        }}
                        onClick={() => setModalRectificar(t)}
                        title="Rectificar Tipo de Trámite ante llamado o error del requirente"
                      >
                        <Edit3 size={13} color="#ffffff" /> Rectificar
                      </button>

                      {/* Cotizar / Cotizado - Verde Esmeralda / Cyan */}
                      {((t.repertorios && t.repertorios.length > 0) || t.exento_repertorio) ? (
                        <button
                          type="button"
                          className="btn btn-sm"
                          style={{
                            background: t.cotizacion?.estado === 'enviada' || t.cotizacion?.estado === 'pagada' ? '#0891b2' : '#059669',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '6px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 5,
                            fontSize: '0.76rem',
                            fontWeight: 700,
                            padding: '5px 10px',
                            boxShadow: '0 1px 2px rgba(5, 150, 105, 0.25)'
                          }}
                          onClick={() => setCotizacionModal({ isOpen: true, tramite: t })}
                          title={t.cotizacion?.estado === 'enviada' || t.cotizacion?.estado === 'pagada' ? 'Ver o editar cotización enviada/pagada' : 'Crear cotización del trámite'}
                        >
                          <DollarSign size={13} color="#ffffff" />
                          {t.cotizacion?.estado === 'enviada' || t.cotizacion?.estado === 'pagada' ? 'Cotizado' : 'Cotizar'}
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-sm"
                          style={{
                            background: '#94a3b8',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '6px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 5,
                            fontSize: '0.76rem',
                            fontWeight: 700,
                            padding: '5px 10px',
                            opacity: 0.5,
                            cursor: 'not-allowed'
                          }}
                          disabled
                          title="Requiere asignar repertorios primero (o marcar exento)"
                        >
                          <DollarSign size={13} color="#ffffff" /> Cotizar
                        </button>
                      )}

                      {/* Historial Cotizaciones (si tiene cotización) - Púrpura */}
                      {t.cotizacion && (
                        <button
                          type="button"
                          className="btn btn-sm"
                          style={{
                            background: '#7c3aed',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '6px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 5,
                            fontSize: '0.76rem',
                            fontWeight: 700,
                            padding: '5px 10px',
                            boxShadow: '0 1px 2px rgba(124, 58, 237, 0.25)'
                          }}
                          onClick={() => setHistorialCotizModal({ isOpen: true, tramite: t })}
                          title="Ver historial de cotizaciones y versiones"
                        >
                          <History size={13} color="#ffffff" /> Historial Cotiz.
                        </button>
                      )}

                      {/* Comprobante(s) de Pago Aceptado(s) */}
                      {comprobantesAceptados.length === 1 && (
                        <a
                          href={comprobantesAceptados[0].comprobante_url}
                          target="_blank"
                          rel="noreferrer"
                          className="btn btn-sm"
                          style={{
                            background: '#059669',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '6px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 5,
                            fontSize: '0.76rem',
                            fontWeight: 700,
                            padding: '5px 10px',
                            textDecoration: 'none',
                            boxShadow: '0 1px 2px rgba(5, 150, 105, 0.25)'
                          }}
                          onClick={(e) => e.stopPropagation()}
                          title="Ver Comprobante de Pago Aceptado en pestaña nueva"
                        >
                          <CheckCircle size={13} color="#ffffff" /> Comprobante
                        </a>
                      )}
                      {comprobantesAceptados.length > 1 && (
                        <div style={{ display: 'inline-flex', gap: '4px', alignItems: 'center' }}>
                          {comprobantesAceptados.map((comp, cIdx) => (
                            <a
                              key={comp.id || cIdx}
                              href={comp.comprobante_url}
                              target="_blank"
                              rel="noreferrer"
                              className="btn btn-sm"
                              style={{
                                background: '#059669',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '6px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4,
                                fontSize: '0.74rem',
                                fontWeight: 700,
                                padding: '5px 8px',
                                textDecoration: 'none',
                                boxShadow: '0 1px 2px rgba(5, 150, 105, 0.25)'
                              }}
                              onClick={(e) => e.stopPropagation()}
                              title={`Ver Comprobante #${cIdx + 1} (${comp.total ? '$' + Number(comp.total).toLocaleString('es-CL') : ''})`}
                            >
                              <CheckCircle size={12} color="#ffffff" /> Comprobante #{cIdx + 1}
                            </a>
                          ))}
                        </div>
                      )}

                      {/* Ver Detalle Completo - Celeste / Cyan */}
                      <button
                        type="button"
                        className="btn btn-sm"
                        style={{
                          background: '#0284c7',
                          color: '#ffffff',
                          border: 'none',
                          borderRadius: '6px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          fontSize: '0.76rem',
                          fontWeight: 700,
                          padding: '5px 10px',
                          boxShadow: '0 1px 2px rgba(2, 132, 199, 0.25)'
                        }}
                        onClick={() => navigate(`/tramite/historial/${t.id}`)}
                        title="Abrir vista detallada"
                      >
                        <ExternalLink size={13} color="#ffffff" /> Detalle
                      </button>
                    </div>

                    {/* Grupo Derecho: Acciones Destructivas + CTA Principal del Flujo */}
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      {/* Botón Rehusar (Estilo Danger Soft/Outline sutil para no distraer) */}
                      {(t.estado?.nombre !== 'Rehusado' && t.estado?.nombre !== 'Completado') && (
                        <button
                          type="button"
                          className="btn btn-sm"
                          style={{
                            background: '#fff',
                            color: '#dc2626',
                            border: '1px solid #fca5a5',
                            borderRadius: '6px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 5,
                            fontSize: '0.76rem',
                            fontWeight: 600,
                            padding: '5px 10px',
                            transition: 'all 0.15s ease'
                          }}
                          onMouseEnter={e => e.currentTarget.style.backgroundColor = '#fef2f2'}
                          onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fff'}
                          onClick={() => { setRejectionModal(t); setRejectionFile(null); }}
                          title="Rehusar trámite oficialmente"
                        >
                          <XCircle size={13} color="#dc2626" /> Rehusar
                        </button>
                      )}

                      {/* Devolución (si corresponde en Pagado o Devuelto) */}
                      {(t.estado?.nombre === 'Pagado' || t.estado?.nombre === 'Devuelto') && (
                        <button
                          type="button"
                          className="btn btn-sm"
                          style={{
                            background: '#fff',
                            color: '#ea580c',
                            border: '1px solid #fdba74',
                            borderRadius: '6px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 5,
                            fontSize: '0.76rem',
                            fontWeight: 600,
                            padding: '5px 10px'
                          }}
                          onClick={() => setDevolucionModal({ isOpen: true, tramite: t })}
                        >
                          <RotateCcw size={13} color="#ea580c" /> Devolución
                        </button>
                      )}

                      {/* === CTA PRINCIPAL CONTEXTUAL === */}
                      {/* Caso A: Validar Comprobante de Pago Recibido */}
                      {t.estado?.nombre === 'Comprobante Pago Recibido' && (
                        t.cotizacion?.estado === 'rechazada' ? (
                          <button
                            type="button"
                            className="btn btn-sm"
                            style={{ background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'not-allowed', fontSize: '0.78rem', fontWeight: 700, padding: '5px 12px', borderRadius: '6px' }}
                            disabled
                            title="Esperando nuevo comprobante del ciudadano"
                          >
                            <AlertCircle size={14} /> Pago Rechazado
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-sm"
                            style={{
                              background: '#0891b2',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                              fontSize: '0.8rem',
                              fontWeight: 700,
                              padding: '5px 14px',
                              boxShadow: '0 2px 4px rgba(8, 145, 178, 0.25)'
                            }}
                            onClick={() => setConfirmarPagoModal({ isOpen: true, tramite: t })}
                          >
                            <CheckCircle size={14} /> Validar Pago
                          </button>
                        )
                      )}

                      {/* Caso B: Finalizar Trámite Pagado */}
                      {(t.estado?.nombre === 'Pagado') && (
                        <button
                          type="button"
                          className="btn btn-sm"
                          style={{
                            background: '#059669',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            fontSize: '0.8rem',
                            fontWeight: 700,
                            padding: '5px 14px',
                            boxShadow: '0 2px 4px rgba(5, 150, 105, 0.25)'
                          }}
                          onClick={() => setFinalizarModal({ isOpen: true, tramite: t })}
                        >
                          <CheckCircle size={14} /> Finalizar Trámite
                        </button>
                      )}

                      {/* Caso C: Subir o Editar Boleta (en Comprobante Pago Recibido, Pagado, Completado o con cotización) */}
                      {(t.estado?.nombre === 'Pagado' || t.estado?.nombre === 'Completado' || t.estado?.nombre === 'Comprobante Pago Recibido' || t.cotizaciones?.some(c => c.estado === 'pagada') || t.cotizacion?.estado === 'pagada' || Boolean(t.cotizacion?.total || t.cotizaciones?.length > 0)) && (() => {
                        const cotiz = t.cotizaciones?.find(c => c.estado === 'pagada') || t.cotizacion || t.cotizaciones?.[0];
                        const tieneBoleta = Boolean(cotiz?.boleta_path || cotiz?.numero_boleta);
                        const numBol = cotiz?.numero_boleta;
                        return (
                          <button
                            type="button"
                            className="btn btn-sm"
                            style={{
                              background: tieneBoleta ? '#ecfdf5' : '#eff6ff',
                              color: tieneBoleta ? '#065f46' : '#1d4ed8',
                              border: `1px solid ${tieneBoleta ? '#a7f3d0' : '#bfdbfe'}`,
                              borderRadius: '6px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                              fontSize: '0.8rem',
                              fontWeight: 700,
                              padding: '5px 12px',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
                            }}
                            title={tieneBoleta ? 'Ver o actualizar boleta tributaria' : 'Subir boleta tributaria del pago'}
                            onClick={() => setBoletaModal({ isOpen: true, item: t, type: 'tramite' })}
                          >
                            <Receipt size={14} color={tieneBoleta ? '#059669' : '#2563eb'} />
                            {tieneBoleta ? (numBol ? `Boleta N° ${numBol}` : 'Boleta Adjunta') : 'Subir Boleta'}
                          </button>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Ficha de Facturación / Boleta */}
                  <div style={{ padding: '0.85rem 1.2rem', borderBottom: '1px solid var(--border-color)', background: '#f8fafc' }}>
                    <FacturacionCardInfo data={t} />
                  </div>

                  {/* Info Grid (Docs & History) */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 0 }}>

                    {/* LEFT COLUMN: Documents */}
                    <div style={{ padding: '1.2rem', borderRight: '1px solid var(--border-color)' }}>
                      <h4 style={{ margin: '0 0 1rem', fontSize: '0.88rem', color: 'var(--primary-dark)', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <FileText size={15} /> Documentos Adjuntos
                      </h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                        <button
                          className="btn btn-outline btn-sm"
                          onClick={() => handleOpenDocs(t)}
                          style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'center', width: '100%', padding: '0.75rem' }}
                        >
                          <Eye size={16} /> Abrir Visualizador ({expandedDetail[t.id]?.documentos?.length ?? t.documentos?.length ?? t.documentos_count ?? 0} archivos)
                        </button>

                        {/* Comprobantes de Pago Aceptados */}
                        {comprobantesAceptados.map((cot, idx) => (
                          <div
                            key={cot.id || idx}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '0.8rem',
                              background: '#ecfdf5',
                              borderRadius: '6px',
                              marginTop: '0.5rem',
                              border: '1px solid #a7f3d0'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', fontWeight: 600, color: '#065f46' }}>
                              <CheckCircle size={16} color="#059669" />
                              <span>
                                {comprobantesAceptados.length > 1 ? `Comprobante de Pago #${idx + 1}` : 'Comprobante de Pago'}
                                {cot.total ? ` ($${Number(cot.total).toLocaleString('es-CL')})` : ''}
                              </span>
                              <span style={{ fontSize: '0.72rem', padding: '2px 6px', background: '#d1fae5', color: '#047857', borderRadius: '4px', fontWeight: 700 }}>
                                {(t.estado?.nombre || '').toLowerCase().includes('recibid') && cot.estado !== 'pagada' ? 'Recibido' : 'Aceptado'}
                              </span>
                            </div>
                            <a
                              href={cot.comprobante_url}
                              target="_blank"
                              rel="noreferrer"
                              className="icon-btn"
                              style={{
                                padding: '6px 12px',
                                fontSize: '0.8rem',
                                color: '#059669',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4,
                                textDecoration: 'none',
                                background: 'white',
                                border: '1px solid #a7f3d0',
                                borderRadius: '4px',
                                fontWeight: 700
                              }}
                              title="Ver comprobante en pestaña"
                            >
                              <Eye size={14} /> Ver
                            </a>
                          </div>
                        ))}

                        {/* Boleta Tributaria / Factura */}
                        {(t.cotizaciones?.some(c => c.boleta_path || c.numero_boleta) || t.cotizacion?.boleta_path || t.cotizacion?.numero_boleta) && (() => {
                          const cot = t.cotizaciones?.find(c => c.boleta_path || c.numero_boleta) || t.cotizacion;
                          return (
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '0.8rem',
                                background: '#f0fdf4',
                                borderRadius: '6px',
                                marginTop: '0.5rem',
                                border: '1px solid #bbf7d0'
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', fontWeight: 600, color: '#166534' }}>
                                <Receipt size={16} color="#16a34a" />
                                <span>
                                  Boleta Electrónica {cot.numero_boleta ? `N° ${cot.numero_boleta}` : ''}
                                </span>
                                <span style={{ fontSize: '0.72rem', padding: '2px 6px', background: '#dcfce7', color: '#15803d', borderRadius: '4px', fontWeight: 700 }}>
                                  Emitida
                                </span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                {cot.boleta_url && (
                                  <a
                                    href={cot.boleta_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="icon-btn"
                                    style={{
                                      padding: '5px 10px',
                                      fontSize: '0.78rem',
                                      color: '#16a34a',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 4,
                                      textDecoration: 'none',
                                      background: 'white',
                                      border: '1px solid #bbf7d0',
                                      borderRadius: '4px',
                                      fontWeight: 700
                                    }}
                                    title="Ver boleta en pestaña"
                                  >
                                    <Eye size={13} /> Ver
                                  </a>
                                )}
                                <button
                                  type="button"
                                  className="icon-btn"
                                  onClick={() => setBoletaModal({ isOpen: true, item: t, type: 'tramite' })}
                                  style={{
                                    padding: '5px 10px',
                                    fontSize: '0.78rem',
                                    color: '#1e40af',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 4,
                                    background: 'white',
                                    border: '1px solid #bfdbfe',
                                    borderRadius: '4px',
                                    fontWeight: 700,
                                    cursor: 'pointer'
                                  }}
                                  title="Editar número o cambiar archivo"
                                >
                                  Editar
                                </button>
                              </div>
                            </div>
                          );
                        })()}
                        {t.documento_rehusado_url && (
                          <div style={{
                            display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 0.8rem',
                            background: '#fee2e2', borderRadius: 8, border: '1px solid #fecaca', marginTop: '0.5rem'
                          }}>
                            <FileText size={16} color="#dc2626" />
                            <span style={{ fontSize: '0.82rem', fontWeight: 600, flex: 1, color: '#991b1b' }}>Documento de Rehuso</span>
                            <a href={t.documento_rehusado_url} target="_blank" rel="noreferrer"
                              style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.78rem', color: '#dc2626', fontWeight: 600 }}>
                              <Eye size={14} /> Ver
                            </a>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* RIGHT COLUMN: History */}
                    <div style={{ padding: '1.2rem' }}>
                      <h4 style={{ margin: '0 0 1rem', fontSize: '0.88rem', color: 'var(--primary-dark)', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <MessageSquare size={15} /> Historial y Comentarios
                      </h4>
                      {!expandedDetail[t.id] ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', fontSize: '0.82rem' }}>
                          <span className="spinner" style={{ width: 16, height: 16 }} /> Cargando historial...
                        </div>
                      ) : (
                        <HistorialPanel
                          steps={expandedDetail[t.id].workflow_steps || []}
                          onReply={(step) => setModalCorreo({ item: t, replyTo: step })}
                          usuarios={usuarios}
                          estados={estados}
                        />
                      )}
                    </div>

                  </div>
                </div>
              )}
            </div>
          );
        })}

          {/* Pagination */}
          <div style={{ padding: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div className="text-muted" style={{ fontSize: '0.85rem' }}>
              Página {page} de {totalPages}
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button className="btn btn-outline btn-sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                <ChevronLeft size={16} /> Anterior
              </button>
              <button className="btn btn-outline btn-sm" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
                Siguiente <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>
      )}

      {modalCorreo && (
        <ModalCorreo
          item={modalCorreo.item || modalCorreo}
          replyTo={modalCorreo.replyTo}
          onClose={() => setModalCorreo(null)}
          onSent={fetchTramites}
          type="tramite"
        />
      )}




      {/* REHUSAR MODAL */}
      {rejectionModal && (
        <div className="modal-overlay" onClick={() => setRejectionModal(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>Rehusar Trámite</h3>
              <button className="modal-close" onClick={() => setRejectionModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="alert alert-warning" style={{ marginBottom: '1.5rem', display: 'flex', gap: '10px' }}>
                <AlertCircle size={20} style={{ flexShrink: 0 }} />
                <p style={{ margin: 0, fontSize: '0.85rem' }}>
                  {rejectionModal.correo 
                    ? <>Al rehusar este trámite, se enviará un correo automático a <strong>{rejectionModal.correo}</strong> con el motivo indicado y el documento adjunto (si corresponde).</>
                    : <>Este trámite <strong>no tiene correo registrado</strong>. El rehusamiento se procesará pero no se enviará notificación automática.</>
                  }
                </p>

              </div>
              <div className="form-group">
                <label>Breve texto explicativo</label>
                <textarea 
                  className="form-control" 
                  rows={4} 
                  placeholder="Describa la razón del rechazo..."
                  value={rejectionMotive}
                  onChange={(e) => setRejectionMotive(e.target.value)}
                  required
                />
              </div>
              
              <div className="form-group" style={{marginTop: '1rem'}}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                  Documento Oficial (Instrumento Público) <span style={{ fontWeight: 400, color: 'var(--gray-500)', fontSize: '0.8rem' }}>(Opcional)</span>
                </label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setRejectionFile(e.target.files[0])}
                  className="form-control"
                  style={{ padding: '8px' }}
                />
                <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: '4px' }}>
                  Adjunte la resolución firmada por el conservador justificando el rehusamiento (Opcional, Max 10MB).
                </div>
              </div>

            </div>
            <div className="modal-footer" style={{ flexWrap: 'nowrap' }}>
              <button className="btn btn-outline" style={{ flex: 1 }} onClick={() => setRejectionModal(null)}>Cancelar</button>
              <button className="btn btn-danger" style={{ flex: 1 }} onClick={handleReject}>Enviar Rehusamiento</button>
            </div>
          </div>
        </div>
      )}

      {/* DOCUMENT VISUALIZER MODAL */}
      {docModal && (
        <VisualizadorDocumentosModal
          isOpen={Boolean(docModal)}
          onClose={() => { setDocModal(null); setSelectedDoc(null); }}
          tramite={docModal}
          initialDoc={selectedDoc}
        />
      )}


      {repertorioModal.isOpen && (
        <GestionarRepertoriosModal
            isOpen={repertorioModal.isOpen}
            onClose={() => setRepertorioModal({ isOpen: false, tramite: null })}
            tramite={repertorioModal.tramite}
            onUpdated={fetchTramites}
        />
      )}

      {cotizacionModal.isOpen && (
        <CotizacionTramiteModal
          tramite={cotizacionModal.tramite}
          onClose={() => setCotizacionModal({ isOpen: false, tramite: null })}
          onUpdate={fetchTramites}
        />
      )}

      {finalizarModal.isOpen && (
        <FinalizarTramiteModal
          tramite={finalizarModal.tramite}
          onClose={() => setFinalizarModal({ isOpen: false, tramite: null })}
          onUpdate={fetchTramites}
        />
      )}

      {confirmarPagoModal.isOpen && (
        <ConfirmarPagoTramiteModal
          tramite={confirmarPagoModal.tramite}
          onClose={() => setConfirmarPagoModal({ isOpen: false, tramite: null })}
          onUpdate={fetchTramites}
        />
      )}

      {devolucionModal.isOpen && (
        <DevolucionTramiteModal
          tramite={devolucionModal.tramite}
          onClose={() => setDevolucionModal({ isOpen: false, tramite: null })}
          onUpdate={fetchTramites}
        />
      )}

      {historialCotizModal.isOpen && (
        <HistorialCotizacionesModal
          isOpen={historialCotizModal.isOpen}
          tramite={historialCotizModal.tramite}
          onClose={() => setHistorialCotizModal({ isOpen: false, tramite: null })}
        />
      )}

      {modalRectificar && (
        <RectificarTramiteModal
          tramite={modalRectificar}
          onClose={() => setModalRectificar(null)}
          onSuccess={handleTramiteRectificado}
        />
      )}

      {boletaModal.isOpen && (
        <SubirBoletaModal
          isOpen={boletaModal.isOpen}
          item={boletaModal.item}
          type="tramite"
          onClose={() => setBoletaModal({ isOpen: false, item: null, type: 'tramite' })}
          onSuccess={fetchTramites}
        />
      )}

    </div>
  );
}
